// src/contexts/MinigameContext.js - 미니게임 전용 상태 관리

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const MinigameContext = createContext();

export const useMinigameContext = () => {
  const context = useContext(MinigameContext);
  if (!context) {
    throw new Error('useMinigameContext must be used within MinigameProvider');
  }
  return context;
};

export const MinigameProvider = ({ children }) => {
  // Pi Clash 게임 전용 상태
  const [piClashScore, setPiClashScore] = useState(() => {
    return parseInt(localStorage.getItem('pi_clash_score') || '0');
  });
  
  const [piClashStage, setPiClashStage] = useState(() => {
    return parseInt(localStorage.getItem('pi_clash_stage') || '1');
  });
  
  const [piClashBombs, setPiClashBombs] = useState(() => {
    return parseInt(localStorage.getItem('pi_clash_bombs') || '3');
  });
  
  const [piClashMaxStage, setPiClashMaxStage] = useState(() => {
    return parseInt(localStorage.getItem('pi_clash_max_stage') || '1');
  });
  
  // Pi Clash 기본 게임 제한 상태 (8시간 기준)
  const [piClashDailyGames, setPiClashDailyGames] = useState(() => {
    const now = Date.now();
    const saved = localStorage.getItem('pi_clash_daily_games');
    const data = saved ? JSON.parse(saved) : { lastReset: now, count: 0 };
    
    // 8시간(28800000ms)이 지났으면 리셋
    if (now - data.lastReset >= 8 * 60 * 60 * 1000) {
      const newData = { lastReset: now, count: 0 };
      localStorage.setItem('pi_clash_daily_games', JSON.stringify(newData));
      return newData;
    }
    
    return data;
  });

  // 실시간 타이머 업데이트를 위한 상태
  const [currentTime, setCurrentTime] = useState(Date.now());

  // 1초마다 현재 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Pi Clash 점수 업데이트 (메인 앱과 분리)
  const updatePiClashScore = useCallback((newScore) => {
    setPiClashScore(newScore);
    localStorage.setItem('pi_clash_score', newScore.toString());
    // 메인 앱 점수에 반영하지 않음
  }, []);

  // Pi Clash 스테이지 업데이트
  const updatePiClashStage = useCallback((newStage) => {
    setPiClashStage(newStage);
    localStorage.setItem('pi_clash_stage', newStage.toString());
  }, []);

  // Pi Clash 폭탄 업데이트
  const updatePiClashBombs = useCallback((newBombs) => {
    setPiClashBombs(newBombs);
    localStorage.setItem('pi_clash_bombs', newBombs.toString());
  }, []);
  
  // Pi Clash 최고 클리어 스테이지 업데이트
  const updatePiClashMaxStage = useCallback((newMaxStage) => {
    console.log('[MinigameContext] updatePiClashMaxStage 호출:', { 
      newMaxStage, 
      currentMaxStage: piClashMaxStage 
    });
    
    setPiClashMaxStage(prevMax => {
      const maxStage = Math.max(prevMax, newMaxStage);
      console.log('[MinigameContext] maxStage 업데이트:', { 
        previousMax: prevMax, 
        newMaxStage, 
        finalMaxStage: maxStage 
      });
      localStorage.setItem('pi_clash_max_stage', maxStage.toString());
      return maxStage;
    });
  }, [piClashMaxStage]);

  // 다음 리셋까지 남은 시간 계산 (실시간 업데이트)
  const getTimeUntilReset = useCallback(() => {
    const timeSinceLastReset = currentTime - piClashDailyGames.lastReset;
    const resetInterval = 8 * 60 * 60 * 1000; // 8시간
    const timeUntilReset = resetInterval - timeSinceLastReset;
    
    if (timeUntilReset <= 0) {
      // 리셋 시간 도달 시 자동 리셋
      const newData = { lastReset: currentTime, count: 0 };
      setPiClashDailyGames(newData);
      localStorage.setItem('pi_clash_daily_games', JSON.stringify(newData));
      return 0;
    }
    
    return timeUntilReset;
  }, [piClashDailyGames.lastReset, currentTime]);

  // Pi Clash 기본 게임 플레이 가능 여부 확인
  const canPlayPiClash = useCallback((ticketCount = 0) => {
    const BASIC_FREE_GAMES = 3;
    
    // 무료 게임이 남아있는지 확인
    if (piClashDailyGames.count < BASIC_FREE_GAMES) {
      return { canPlay: true, reason: 'free', remaining: BASIC_FREE_GAMES - piClashDailyGames.count };
    }
    
    // 티켓이 있는지 확인
    if (ticketCount > 0) {
      return { canPlay: true, reason: 'ticket', remaining: ticketCount };
    }
    
    return { canPlay: false, reason: 'limit', remaining: 0 };
  }, [piClashDailyGames.count]);

  // Pi Clash 게임 플레이 (기본 제한)
  const playPiClashGame = useCallback((ticketCount = 0, onTicketUsed = null) => {
    const BASIC_FREE_GAMES = 3;
    const playStatus = canPlayPiClash(ticketCount);
    
    if (!playStatus.canPlay) {
      return { success: false, reason: 'limit' };
    }
    
    // 무료 게임 사용
    if (playStatus.reason === 'free') {
      const newCount = piClashDailyGames.count + 1;
      const newData = { ...piClashDailyGames, count: newCount };
      setPiClashDailyGames(newData);
      localStorage.setItem('pi_clash_daily_games', JSON.stringify(newData));
      
      return { success: true, reason: 'free', remaining: BASIC_FREE_GAMES - newCount };
    }
    
    // 티켓 사용
    if (playStatus.reason === 'ticket') {
      // 티켓 사용 콜백 호출
      if (onTicketUsed) {
        onTicketUsed(1); // 1개 티켓 사용
      }
      return { success: true, reason: 'ticket', remaining: ticketCount - 1 };
    }
    
    return { success: false, reason: 'error' };
  }, [canPlayPiClash, piClashDailyGames]);

  // 랭킹 시스템 없음 - 개인 기록만 관리 (최고 점수 기준으로 촘촘한 등급)
  const getPiClashRank = useCallback((score) => {
    if (score < 500) return 'tier_bronze';
    if (score < 1500) return 'tier_silver';
    if (score < 3000) return 'tier_gold';
    if (score < 6000) return 'tier_platinum';
    if (score < 12000) return 'tier_diamond';
    if (score < 25000) return 'tier_master';
    return 'tier_grandmaster';
  }, []);

  const value = {
    // Pi Clash 상태
    piClashScore,
    piClashStage,
    piClashBombs,
    piClashMaxStage,
    piClashDailyGames,
    
    // Pi Clash 함수
    updatePiClashScore,
    updatePiClashStage,
    updatePiClashBombs,
    updatePiClashMaxStage,
    canPlayPiClash,
    playPiClashGame,
    getPiClashRank,
    getTimeUntilReset,
  };

  return (
    <MinigameContext.Provider value={value}>
      {children}
    </MinigameContext.Provider>
  );
};
